/**
 * Onglet « Effectifs adhérents ».
 */

import {
  dateFr,
  entier,
  euros,
  eurosCentimes,
  eurosSigne,
  keuros,
  libelleVerdict,
  nombre,
  pourcent,
} from '@scc/core';
import type { Charge } from '../api.js';
import { h } from '../dom.js';
import { TEINTES, graphiqueLignes, legende } from '../graphiques.js';
import { badge, barreExport, enteteSection, insight, kpi, noteLecture, regleAccent } from '../composants.js';

const COULEURS: Record<string, string> = {
  sc1: TEINTES.slate,
  sc2: TEINTES.green,
  sc3: TEINTES.amber,
};

export function vueAdherents(d: Charge): HTMLElement {
  const a = d.adherents;
  const clotures = a.seriesClotures;
  const anneesProjetees = d.trajectoires[0]?.annees.map((x) => x.annee) ?? [];
  const toutesAnnees = [...clotures.map((s) => s.annee), ...anneesProjetees];

  const vides = (n: number): (number | null)[] => Array.from({ length: n }, () => null);
  const raccorder = (valeurs: readonly number[]): (number | null)[] => {
    const o: (number | null)[] = [...vides(clotures.length), ...valeurs];
    // Raccordement visuel : la projection part du dernier point constaté.
    o[clotures.length - 1] = clotures[clotures.length - 1]?.payeurs ?? null;
    return o;
  };
  const raccorderProduit = (valeurs: readonly number[]): (number | null)[] => {
    const o: (number | null)[] = [...vides(clotures.length), ...valeurs];
    o[clotures.length - 1] = clotures[clotures.length - 1]?.produitMoyen ?? null;
    return o;
  };

  const trajectoiresAffichees = d.trajectoires.filter((t) => t.cle !== 'sim');

  const graphEffectifs = h(
    'div',
    {},
    graphiqueLignes(
      toutesAnnees,
      [
        {
          nom: 'Adhérents payeurs constatés',
          couleur: TEINTES.charcoal,
          epaisseur: 3,
          valeurs: [...clotures.map((s) => s.payeurs), ...vides(anneesProjetees.length)],
        },
        ...trajectoiresAffichees.map((t) => ({
          nom: t.libelle,
          couleur: COULEURS[t.cle] ?? TEINTES.slate,
          pointilles: '6 4',
          valeurs: raccorder(t.annees.map((x) => x.adherents)),
        })),
      ],
      { unite: 'effectif', depuisZero: true, titre: 'Adhérents payeurs et projections', enteteAbscisse: 'Exercice' },
    ),
    legende([
      { nom: 'Adhérents payeurs', couleur: TEINTES.charcoal },
      ...trajectoiresAffichees.map((t) => ({ nom: t.libelle, couleur: COULEURS[t.cle] ?? TEINTES.slate })),
    ]),
  );

  const graphProduit = h(
    'div',
    {},
    graphiqueLignes(
      toutesAnnees,
      [
        {
          nom: 'Produit moyen constaté',
          couleur: TEINTES.charcoal,
          epaisseur: 3,
          valeurs: [...clotures.map((s) => s.produitMoyen), ...vides(anneesProjetees.length)],
        },
        ...trajectoiresAffichees.map((t) => ({
          nom: t.libelle,
          couleur: COULEURS[t.cle] ?? TEINTES.slate,
          pointilles: '6 4',
          valeurs: raccorderProduit(a.produitsMoyensScenarios[t.cle] ?? []),
        })),
      ],
      { unite: 'euros', depuisZero: true, titre: 'Produit moyen par adhérent', enteteAbscisse: 'Exercice' },
    ),
    legende([
      { nom: 'Produit moyen constaté', couleur: TEINTES.charcoal },
      ...trajectoiresAffichees.map((t) => ({ nom: t.libelle, couleur: COULEURS[t.cle] ?? TEINTES.slate })),
    ]),
  );

  const tableSerie = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, 'Adhérents payeurs et produit moyen par exercice'),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Exercice'),
        h('th', { scope: 'col' }, 'Adhérents payeurs'),
        h('th', { scope: 'col' }, 'Cotisations acquittées'),
        h('th', { scope: 'col' }, 'Produit moyen'),
        h('th', { scope: 'col' }, 'Évolution'),
      ),
    ),
    h(
      'tbody',
      {},
      ...clotures.map((s, i) => {
        const precedent = clotures[i - 1];
        const evol = precedent && precedent.produitMoyen > 0 ? s.produitMoyen / precedent.produitMoyen - 1 : null;
        return h(
          'tr',
          {},
          h('th', { scope: 'row' }, h('strong', {}, String(s.annee))),
          h('td', {}, entier(s.payeurs)),
          h('td', { classe: 'num' }, eurosCentimes(s.cotisationsAcquittees)),
          h('td', { classe: 'num' }, eurosCentimes(s.produitMoyen)),
          h(
            'td',
            {},
            evol === null
              ? badge('neutre', 'Référence')
              : badge(evol >= 0 ? 'favorable' : 'alerte', `${evol >= 0 ? '▲' : '▼'} ${pourcent(evol, 0)}`),
          ),
        );
      }),
    ),
  );

  const solidaires = clotures.filter((s) => s.cotisationSolidaire > 0);

  const tableRecouvrement = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, `Recouvrement de l'exercice ${d.meta.exerciceCourant} par collège`),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Collège'),
        h('th', { scope: 'col' }, 'Cotisations appelées'),
        h('th', { scope: 'col' }, 'Acquittées'),
        h('th', { scope: 'col' }, 'Taux'),
      ),
    ),
    h(
      'tbody',
      {},
      ...a.ventilationCollege.map((v) =>
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, v.college),
          h('td', {}, entier(v.appels)),
          h('td', {}, entier(v.payeurs)),
          h('td', {}, pourcent(v.appels > 0 ? v.payeurs / v.appels : 0, 0)),
        ),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, h('strong', {}, 'Total')),
        h('td', {}, h('strong', {}, entier(a.appelsExerciceCourant))),
        h('td', {}, h('strong', {}, entier(a.serieCourante?.payeurs ?? 0))),
        h('td', {}, h('strong', {}, pourcent(a.serieCourante?.tauxRecouvrement ?? 0, 0))),
      ),
    ),
  );

  const tableTypes = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, `Ventilation par type de cotisant, exercice ${d.meta.exerciceCourant}`),
    h(
      'thead',
      {},
      h('tr', {}, h('th', { scope: 'col' }, 'Type de cotisant'), h('th', { scope: 'col' }, 'Appelées'), h('th', { scope: 'col' }, 'Acquittées')),
    ),
    h(
      'tbody',
      {},
      ...a.ventilationTypeCotisant.map((v) =>
        h('tr', {}, h('th', { scope: 'row' }, v.type), h('td', {}, entier(v.appels)), h('td', {}, entier(v.payeurs))),
      ),
    ),
  );

  // ── Où en sommes-nous ───────────────────────────────────────────────────
  //
  // Deux signaux distincts. L'assiette d'appel commande le produit de
  // l'exercice ; le rythme de facturation ne fait que décaler l'encaissement.
  // Les confondre ferait passer un problème de calendrier pour un problème de
  // fond, ou l'inverse.
  const av = a.avancement;
  const tonVerdict =
    av.verdict === 'en_retard' ? 'alert' : av.verdict === 'en_avance' ? 'progress' : 'neutral';

  const avancement = h(
    'div',
    {},
    h(
      'div',
      { classe: 'kpi-grid' },
      kpi(tonVerdict, entier(av.appels), 'appels', 'Assiette d’appel de l’exercice', {
        sens: 'neutre',
        texte: `taux de recouvrement retenu ${pourcent(av.tauxRecouvrement, 1)}`,
      }),
      kpi('neutral', entier(av.payeursProjetes), 'payeurs', 'Effectif projeté à la clôture', {
        sens: 'neutre',
        texte: `dont ${entier(av.payeurs)} ont déjà réglé`,
      }),
      kpi(tonVerdict, keuros(av.cotisationsProjetees), null, 'Cotisations projetées', {
        sens: av.ecartCible >= 0 ? 'up' : 'down',
        texte: `${eurosSigne(av.ecartCible)} contre la cible de ${keuros(av.cible)}`,
      }),
      kpi(
        (av.ratioFacturation ?? 1) < 0.9 ? 'warning' : 'neutral',
        av.ratioFacturation === null ? '—' : pourcent(av.ratioFacturation, 0),
        null,
        'Rythme de facturation',
        {
          sens: (av.ratioFacturation ?? 1) < 1 ? 'down' : 'up',
          texte: `${entier(av.facturesEmises)} factures contre ${entier(av.facturesPrecedentes)} un an plus tôt`,
        },
      ),
    ),
    insight(
      av.verdict === 'en_retard' ? 'negative' : av.verdict === 'en_avance' ? 'positive' : 'info',
      av.verdict === 'en_retard' ? '⚠' : av.verdict === 'en_avance' ? '✓' : '≡',
      `**${libelleVerdict(av.verdict)}.** ${entier(av.appels)} cotisations sont ouvertes sur l'exercice. ` +
        `Au taux de recouvrement constaté (${pourcent(av.tauxRecouvrement, 1)}) et au produit moyen de ` +
        `${euros(av.produitMoyen)}, elles donneraient ${entier(av.payeursProjetes)} payeurs et ` +
        `${euros(av.cotisationsProjetees)} — ${eurosSigne(av.ecartCible)} par rapport aux ${euros(av.cible)} ` +
        `attendus. ` +
        (av.appelsManquants > 0
          ? `Il manquerait ${entier(av.appelsManquants)} appels pour rendre la cible atteignable.`
          : `L'assiette suffit à la cible.`) +
        (av.ratioFacturation !== null && av.ratioFacturation < 0.9
          ? ` La facturation, elle, n'est qu'à ${pourcent(av.ratioFacturation, 0)} du rythme de l'an dernier : ` +
            `c'est un décalage de calendrier, pas une perte de produit — à condition qu'il se rattrape.`
          : ''),
    ),
  );

  // ── Rythme d'encaissement, mois par mois ───────────────────────────────
  const exploitables = a.rythme.filter((r) => r.exploitable);
  const rythme = h(
    'div',
    {},
    exploitables.length === 0
      ? h('p', { classe: 'note-lecture' }, 'Aucun exercice ne porte assez de dates de règlement.')
      : graphiqueLignes(
          Array.from({ length: 12 }, (_, i) => i + 1),
          exploitables.map((r, i) => ({
            nom: `Cumul ${r.exercice}`,
            couleur: [TEINTES.rose, TEINTES.blue, TEINTES.green, TEINTES.amber][i % 4] ?? TEINTES.rose,
            valeurs: r.points.map((p) => p.montantCumule),
          })),
          {
            titre: 'Encaissement cumulé des cotisations, mois par mois',
            enteteAbscisse: 'Mois',
            unite: 'euros',
            depuisZero: true,
          },
        ),
    h(
      'table',
      { classe: 'comp-table encadre-tableau' },
      h('caption', { classe: 'invisible' }, 'Couverture des dates de règlement par exercice'),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Exercice'),
          h('th', { scope: 'col' }, 'Adhérents payeurs'),
          h('th', { scope: 'col' }, 'Cotisations acquittées'),
          h('th', { scope: 'col' }, 'Règlements datés'),
          h('th', { scope: 'col' }, 'Courbe exploitable'),
        ),
      ),
      h(
        'tbody',
        {},
        ...a.rythme.map((r) =>
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, String(r.exercice)),
            h('td', { classe: 'num' }, entier(r.totalPayeurs)),
            h('td', { classe: 'num' }, euros(r.totalMontant)),
            h('td', { classe: 'num' }, pourcent(r.couvertureDates, 0)),
            h('td', {}, badge(r.exploitable ? 'favorable' : 'vigilance', r.exploitable ? 'oui' : 'non')),
          ),
        ),
      ),
    ),
    noteLecture(
      `Les dates de règlement ne sont renseignées dans Directus que depuis ${exploitables[0]?.exercice ?? d.meta.exerciceCourant}. ` +
        `Aucune saisonnalité historique n'est donc établissable, et l'application n'en invente pas : c'est ` +
        `pourquoi le jugement ci-dessus porte sur l'assiette d'appel et le taux de recouvrement, qui eux sont ` +
        `connus sur huit exercices.`,
    ),
  );

  // ── Historique complet ─────────────────────────────────────────────────
  const historique = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'comp-table encadre-tableau' },
      h('caption', { classe: 'invisible' }, 'Historique des effectifs et des cotisations'),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Exercice'),
          h('th', { scope: 'col' }, 'Cotisations appelées'),
          h('th', { scope: 'col' }, 'Adhérents payeurs'),
          h('th', { scope: 'col' }, 'Taux de recouvrement'),
          h('th', { scope: 'col' }, 'Cotisations acquittées'),
          h('th', { scope: 'col' }, 'Produit moyen'),
          h('th', { scope: 'col' }, 'Variation du produit'),
          h('th', { scope: 'col' }, 'Doublons écartés'),
        ),
      ),
      h(
        'tbody',
        {},
        ...a.series.map((s, i) => {
          const precedent = a.series[i - 1];
          const variation =
            precedent && precedent.cotisationsAcquittees > 0
              ? s.cotisationsAcquittees / precedent.cotisationsAcquittees - 1
              : null;
          return h(
            'tr',
            { classe: s.annee === d.meta.exerciceCourant ? 'ligne-alerte' : '' },
            h('th', { scope: 'row' }, String(s.annee)),
            h('td', { classe: 'num' }, entier(s.appels)),
            h('td', { classe: 'num' }, entier(s.payeurs)),
            h('td', { classe: 'num' }, pourcent(s.tauxRecouvrement, 1)),
            h('td', { classe: 'num' }, euros(s.cotisationsAcquittees)),
            h('td', { classe: 'num' }, euros(s.produitMoyen)),
            h('td', { classe: 'num' }, variation === null ? '—' : pourcent(variation, 1)),
            h('td', { classe: 'num' }, entier(s.doublonsEcartes)),
          );
        }),
      ),
    ),
  );

  return h(
    'div',
    { classe: 'page', id: 'page-adherents', role: 'region', 'aria-label': 'Adhérents' },
    h(
      'section',
      {},
      enteteSection(
        1,
        `Où en sommes-nous au ${dateFr(d.meta.dateArrete)}`,
        'Les projections sont-elles probables ?',
      ),
      avancement,
      regleAccent('Rythme d’encaissement'),
      rythme,
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(
        2,
        'Historique des effectifs et des cotisations',
        `Exercice par exercice depuis ${a.series[0]?.annee ?? ''}`,
      ),
      historique,
      noteLecture(
        `Les cotisations solidaires de 2020 et 2021, appelées au taux réduit de 0,1 %, sont exclues du produit ` +
          `moyen et signalées séparément. L'exercice en cours est signalé : ses cotisations ne sont pas closes.`,
      ),
      barreExport('adherents', 'Historique des adhérents'),
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(3, 'Les adhérents, variable déterminante', `Adhérents payeurs depuis ${clotures[0]?.annee ?? ''} et hypothèses de projection`),
      graphEffectifs,
      noteLecture(
        `Note de lecture : sont comptés les adhérents ayant effectivement acquitté leur cotisation de l'exercice. ` +
          `${entier(clotures[0]?.payeurs ?? 0)} payeurs en ${clotures[0]?.annee ?? ''}, un maximum de ` +
          `${entier(a.recordHistorique)} atteint sur la période, ` +
          `${entier(clotures[clotures.length - 1]?.payeurs ?? 0)} en ${clotures[clotures.length - 1]?.annee ?? ''}. ` +
          `Les doublons de structure sur un même exercice comptent pour un.`,
      ),
      insight(
        'warning',
        '⚠',
        `**Les scénarios du classeur extrapolent au-delà du record historique :** le record constaté est de ` +
          `${entier(a.recordHistorique)} adhérents payeurs. Le scénario 1 vise ` +
          `${entier(d.trajectoires.find((t) => t.cle === 'sc1')?.annees[anneesProjetees.length - 1]?.adherents ?? 0)} en ` +
          `${anneesProjetees[anneesProjetees.length - 1]}, le scénario 2 ` +
          `${entier(d.trajectoires.find((t) => t.cle === 'sc2')?.annees[anneesProjetees.length - 1]?.adherents ?? 0)}. ` +
          `Le scénario 3 retient ` +
          `${entier(d.trajectoires.find((t) => t.cle === 'sc3')?.annees[anneesProjetees.length - 1]?.adherents ?? 0)} ` +
          `adhérents payeurs, atteints par paliers réguliers depuis les ${entier(a.payeursProjetes)} attendus en ` +
          `${d.meta.exerciceCourant}.`,
      ),
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(4, 'Le produit moyen par adhérent', 'Cotisations acquittées rapportées au nombre d’adhérents payeurs'),
      h(
        'p',
        { classe: 'text-block' },
        `La cotisation représente un taux fixe des produits déclarés par l'adhérent : le barème n'a pas été modifié. ` +
          `La progression constatée tient donc au fait que les produits déclarés dépassent les estimations sur ` +
          `lesquelles les prévisionnels avaient été construits.`,
      ),
      graphProduit,
      noteLecture(
        `Note de lecture : produit moyen = cotisations acquittées au titre de l'exercice divisées par le nombre ` +
          `d'adhérents payeurs. ` +
          (solidaires.length > 0
            ? `Les cotisations solidaires au taux réduit — ${solidaires.map((s) => `${eurosCentimes(s.cotisationSolidaire)} en ${s.annee}`).join(', ')} — sont exclues du calcul.`
            : ''),
      ),
      tableSerie,
      barreExport('adherents', 'Adhérents payeurs et produit moyen'),
      insight(
        'info',
        'ℹ',
        `**La pente retenue par le scénario 3 :** en écartant les exercices ` +
          `${a.regression.exercicesEcartes.join(' et ')}, atypiques du fait de la crise sanitaire et de la cotisation ` +
          `solidaire, le produit moyen progresse de **${nombre(a.regression.pente, 2)} € par an** de façon très ` +
          `régulière (coefficient de détermination de ${nombre(a.regression.r2, 2)}). Le scénario 3 prolonge cette ` +
          `pente à partir du produit moyen implicite de ${d.meta.exerciceCourant}, soit ` +
          `${eurosCentimes(a.produitMoyenImplicite)}. Il ne fait aucune hypothèse de barème nouveau.`,
      ),
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(5, 'Le recouvrement de l’exercice en cours', 'L’adhérent cotisant est l’adhérent qui a payé sa cotisation'),
      h(
        'p',
        { classe: 'text-block' },
        `${entier(a.appelsExerciceCourant)} cotisations ont été appelées au titre de l'exercice ` +
          `${d.meta.exerciceCourant}. À la date d'arrêté, ${entier(a.serieCourante?.payeurs ?? 0)} étaient acquittées, ` +
          `pour ${eurosCentimes(a.serieCourante?.cotisationsAcquittees ?? 0)}. Le taux de recouvrement constaté en ` +
          `${clotures[clotures.length - 1]?.annee ?? ''}, ${pourcent(a.tauxRecouvrementPrecedent)}, appliqué aux appels ` +
          `de l'exercice, conduit à ${entier(a.payeursProjetes)} adhérents payeurs en fin d'exercice. Les ` +
          `${euros(d.previsionnelVote.cotisations)} de cotisations votés correspondent alors à un produit ` +
          `moyen de ${eurosCentimes(a.produitMoyenImplicite)}.`,
      ),
      h('div', { classe: 'two-col' }, h('div', {}, tableRecouvrement), h('div', {}, tableTypes)),
      insight(
        'warning',
        '⚠',
        `**L'enjeu se déplace vers le recouvrement :** le taux de règlement des cotisations appelées s'établit entre ` +
          `${pourcent(Math.min(...clotures.map((s) => s.tauxRecouvrement)))} et ` +
          `${pourcent(Math.max(...clotures.map((s) => s.tauxRecouvrement)))} sur la série. Chaque point de recouvrement ` +
          `gagné vaut environ ${nombre(a.appelsExerciceCourant / 100, 1)} adhérents payeurs et ` +
          `${euros((a.appelsExerciceCourant / 100) * a.produitMoyenImplicite)} de produit.`,
      ),
    ),
  );
}
